import * as React from "react";
import {
    QueryFilter,
    QueryOrderDirection
} from "../../api/apiTypes";
import { Query, QueryOrder, QueryPage } from "./Query";
import { QueryTable } from "./QueryTable";
import { getQuerySorting } from "./ReducerUtilities";
import { QueryTableColumnProps } from "./QueryTable.interfaces";

interface LocalQueryTableProps {
    collection: any[];
    defaultPageSize?: number;
    filters: QueryFilter[];
    onFilter: (member: string) => void;
    defaultSort?: { [key: string]: QueryOrderDirection };
    children: Array<React.ReactElement<QueryTableColumnProps>>;
    query: Query<any>;
    rowUniqueIdentifier: (row) => void;
}

interface LocalQueryTableState {
    query: Query<any>;
    collection: any[];
}

export class LocalQueryTable extends React.Component<
    LocalQueryTableProps,
    LocalQueryTableState
> {
    static defaultProps: Partial<LocalQueryTableProps> = {
        defaultPageSize: 10
    };

    static getDerivedStateFromProps(
        props: LocalQueryTableProps,
        state: LocalQueryTableState
    ) {
        let isCollectionChanged = false;
        let isFiltersChanged = false;
        let query = state.query;

        if (props.collection !== state.collection) {
            let currentPage = 1;
            if (props.collection.length < state.query?.Page?.Current * state.query?.Page?.Length - 1) {
                currentPage = 1;
            } else {
                currentPage = state.query
                    ? state.query.Page
                        ? state.query.Page.Current
                        : 1
                    : 1;
            }
            const queryOrder = state.query ? state.query.Order : null;
            query = new Query(
                props.collection,
                currentPage,
                props.defaultPageSize,
                queryOrder,
                props.defaultSort
            );

            isCollectionChanged = true;
        }

        if (props.filters !== query.Filters) {
            isFiltersChanged = true;
        }

        return {
            collection: isCollectionChanged
                ? props.collection
                : state.collection,
            query: {
                ...query,
                Filters: isFiltersChanged
                    ? props.filters
                    : state.query
                        ? state.query.Filters
                        : null
            }
        };
    }

    getQuery() {
        return this.state.query;
    }

    constructor(props) {
        super(props);

        this.state = {
            query: props.query,
            collection: null
        };
    }

    handlePage = (page: number) => {
        const query = new Query(
            this.props.collection,
            page,
            this.props.defaultPageSize,
            this.state.query.Order
        );

        this.setState({
            query
        });
    };

    handleSort = (member: string, direction?: QueryOrderDirection) => {

        const order = this.getQueryOrder(member, direction);
        this.props.collection.sort(this.sortCompare(order));

        const result = this.props.collection.slice(
            this.state.query.Page.Length * (this.state.query.Page.Current - 1),
            this.state.query.Page.Length * (this.state.query.Page.Current - 1) +
                this.state.query.Page.Length
        );

        this.setState({
            query: {
                ...this.state.query,
                Order: order,
                Data: result
            }
        });
    };

    handleFilter = (member: string) => {
        this.setState({
            query: {
                ...this.state.query,
                Page: {
                    ...this.state.query.Page,
                    Current: 1
                }
            }
        });
        this.props.onFilter(member);
    };

    private getQueryOrder(member: string, direction?: QueryOrderDirection) {
        return direction
            ? new QueryOrder(member, direction)
            : getQuerySorting(this.state.query, member);
    }

    render() {
        return (
            <QueryTable
                query={this.state.query}
                rowUniqueIdentifier={this.props.rowUniqueIdentifier}
                onPage={this.handlePage}
                onFilter={this.handleFilter}
                onSort={m => this.handleSort(m)}>
                {this.props.children}
            </QueryTable>
        );
    }

    componentDidMount() {
        this.handleDefaultSort();
    }

    private handleDefaultSort() {
        if (this.props.defaultSort) {
            const members = Object.keys(this.props.defaultSort);
            const direction = this.props.defaultSort[members[0]];
            this.handleSort(members[0], direction);
        }
    }

    private sortCompare(queryOrder: QueryOrder) {
        return (a: object, b: object): number => {
            if (
                !a.hasOwnProperty(queryOrder.Member) ||
                !b.hasOwnProperty(queryOrder.Member)
            ) {
                return 0;
            }

            const varA =
                typeof a[queryOrder.Member] === "string"
                    ? a[queryOrder.Member].toUpperCase()
                    : a[queryOrder.Member];
            const varB =
                typeof b[queryOrder.Member] === "string"
                    ? b[queryOrder.Member].toUpperCase()
                    : b[queryOrder.Member];

            let comparison = 0;
            if (varA > varB) {
                comparison = 1;
            } else if (varA < varB) {
                comparison = -1;
            }

            return queryOrder.Direction === QueryOrderDirection.Descending
                ? comparison * -1
                : comparison;
        };
    }
}
